import type { InfiniteData } from "@tanstack/react-query";
import type { MessagePage } from "@bw/shared/types/generated";

export type MessageHistoryData = InfiniteData<MessagePage> & {
  /** Immutable exact read commands returned by individual server pages. */
  readAuthorityBatches?: string[][];
};

type NetworkAuthorityPage = MessagePage & {
  /** Internal provenance stripped before a page is stored as display history. */
  __bwServerReadAuthority?: string[];
};

export function withMessagePageReadAuthority(page: MessagePage): MessagePage {
  return {
    ...page,
    __bwServerReadAuthority: [...page.read_message_ids],
  } as NetworkAuthorityPage;
}

export function networkReadAuthority(page: MessagePage): string[][] {
  const authority = (page as NetworkAuthorityPage).__bwServerReadAuthority;
  return authority && authority.length > 0 ? [[...authority]] : [];
}

export function hasNetworkReadAuthority(page: MessagePage): boolean {
  return (page as NetworkAuthorityPage).__bwServerReadAuthority !== undefined;
}

export function withoutNetworkAuthority(page: MessagePage): MessagePage {
  const { __bwServerReadAuthority: _authority, ...stored } =
    page as NetworkAuthorityPage;
  return stored;
}

export function exactReadAuthority(
  data: InfiniteData<MessagePage> | undefined,
): string[][] {
  const stored = (data as MessageHistoryData | undefined)?.readAuthorityBatches;
  const source =
    stored ??
    data?.pages.flatMap((page) => {
      const network = networkReadAuthority(page);
      return network.length > 0 ? network : [page.read_message_ids];
    }) ??
    [];
  const seen = new Set<string>();
  return source
    .filter((batch) => batch.length > 0)
    .filter((batch) => {
      const signature = JSON.stringify(batch);
      if (seen.has(signature)) return false;
      seen.add(signature);
      return true;
    })
    .map((batch) => [...batch]);
}

export function combineReadAuthority(...groups: string[][][]): string[][] {
  const seen = new Set<string>();
  const combined: string[][] = [];
  for (const batch of groups.flat()) {
    if (batch.length === 0) continue;
    const signature = JSON.stringify(batch);
    if (seen.has(signature)) continue;
    seen.add(signature);
    combined.push([...batch]);
  }
  return combined;
}

export function retainStillEligibleAuthority(
  batches: string[][],
  authoritativePages: MessagePage[],
): string[][] {
  const ineligibleIds = new Set<string>();
  for (const page of authoritativePages) {
    const eligible = new Set(page.read_message_ids);
    for (const item of page.items) {
      if (!eligible.has(item.message_id)) ineligibleIds.add(item.message_id);
    }
  }
  return batches.filter(
    (batch) => !batch.some((messageId) => ineligibleIds.has(messageId)),
  );
}

export function messageReadAuthority(
  data: InfiniteData<MessagePage> | undefined,
): string[][] {
  return exactReadAuthority(data);
}
